/** @jsxImportSource @emotion/react */

import { useCallback } from 'react';
import _get from 'lodash/get';
import useRouter from 'use-react-router';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';

import Table, { useTable } from 'common/components/table';
import { useDispatch } from 'react-redux';
import { getUpdatedTableFiltersParams } from 'common/utils/filters/helpers';
import { getPurchasingStoresEquivalents } from 'store/purchasing-stores/actions';
import TextWithTooltip from 'common/components/general/TextWithTooltip';

const EquivalentTable = ({ organizationId, storeGroupId }) => {
  const dispatch = useDispatch();
  const { location } = useRouter();

  const fetchTableData = useCallback(
    async params => {
      const filters = [..._get(params, 'filters', [])];

      if (!organizationId) return;

      const parseParams = {
        ...params,
        paging: {
          per_page: params?.paging?.per_page || 50,
          current_page: params?.paging?.current_page || 1
        }
      };

      await dispatch(
        getPurchasingStoresEquivalents({
          ...parseParams,
          id: storeGroupId,
          organization_id: organizationId,
          filters
        })
      );
    },
    [dispatch, organizationId, storeGroupId]
  );

  const table = useTable({
    label: 'equivalent_items',
    requestTableListFrom: fetchTableData,
    defaultRequestParams: { visible: false, paging: true, sorting: true, filters: false },
    columns: [
      {
        header: 'Item Group',
        key: 'store_group_id',
        width: 5,
        sort: true
      },
      {
        header: 'Code',
        key: 'code',
        type: 'string',
        sort: true,
        width: 1
      },
      {
        header: 'Item',
        key: 'description',
        type: 'string',
        sort: true
      }
    ],
    top: {}
  });

  return (
    <div
      css={css`
        .app-table--head {
          .cell {
            background-color: white !important;
            color: #354069 !important;

            .svg-wrap {
              color: #354069 !important;
            }
          }
        }
      `}
    >
      <Table
        handleTableSort={({ sorting }) => table.refetchData({ sorting })}
        handleTableSearch={search => table.refetchData({ search })}
        loader
        handleTableApplyFilters={() =>
          table.refetchData({ ...getUpdatedTableFiltersParams(table, location) })
        }
        hideTableReorder
        hideTableFilters
        hideBottomPagination
        hideTableSearch
        grayBg
        rows={{
          store_group_id: data => (
            <TextWithTooltip className="d-flex align-items-center cms-4">
              {data?.store_group?.code ? `${data?.store_group?.code}. ` : ''}
              {data?.store_group?.name || '-'}
            </TextWithTooltip>
          ),
          code: data => (
            <TextWithTooltip className="d-flex align-items-center cms-4">
              {data?.code || '-'}
            </TextWithTooltip>
          ),
          store_id: data => (
            <TextWithTooltip className="d-flex align-items-center cms-4">
              {data?.description || '-'}
            </TextWithTooltip>
          )
        }}
        {...table}
      />
    </div>
  );
};

export default EquivalentTable;

EquivalentTable.propTypes = {
  organizationId: PropTypes.number.isRequired,
  storeGroupId: PropTypes.number.isRequired
};
